import { ApiError } from '@mrigl/api-client';
import { GOVERNORATES, governorateName, type GovernorateCode, type Locale } from '@mrigl/shared';
import { errorMessage } from '../../api.js';
import type { I18n } from '../../i18n.js';

/** "19" or "7,5" (percent) → 1900 / 750 basis points; null when invalid. */
export function toBasisPoints(text: string): number | null {
  const normalized = text.trim().replace(',', '.');
  if (!/^[0-9]{1,3}(\.[0-9]{1,2})?$/.test(normalized)) return null;
  const value = Math.round(Number(normalized) * 100);
  return value <= 10_000 ? value : null;
}

/** 1900 → "19", 750 → "7.5" for editing and display. */
export function fromBasisPoints(bp: number): string {
  return String(bp / 100);
}

/** Governorates sorted by their name in the admin language. */
export function governorateOptions(locale: Locale): { value: GovernorateCode; label: string }[] {
  return GOVERNORATES.map((item) => ({
    value: item.code,
    label: governorateName(item.code, locale),
  })).sort((a, b) => a.label.localeCompare(b.label, locale));
}

/**
 * Tunis wall-clock date "2026-10-20" → the UTC instant at which that day starts there
 * (`endOfDay` = the next day's start, so a code is valid through the whole last day).
 */
export function tunisDayStart(date: string, endOfDay = false): string | null {
  const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(date);
  if (match === null) return null;
  const [, year, month, day] = match.map(Number) as [number, number, number, number];
  const guess = Date.UTC(year, month - 1, day + (endOfDay ? 1 : 0));
  // Offset of Africa/Tunis at that instant, read from the platform time-zone database.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Africa/Tunis',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(guess));
  const part = (type: string) => Number(parts.find((item) => item.type === type)?.value ?? '0');
  const local = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
  );
  return new Date(guess - (local - guess)).toISOString();
}

/** UTC instant → the Tunis calendar date "YYYY-MM-DD" it falls on (`endOfDay`: the day before). */
export function tunisDate(instant: string, endOfDay = false): string {
  const value = new Date(Date.parse(instant) - (endOfDay ? 1 : 0));
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Tunis',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(value);
}

/** Problem codes of the checkout API mapped to messages of the given screen. */
export function checkoutError(
  t: I18n['t'],
  failure: unknown,
  screen: 'checkout' | 'discount',
): string {
  if (failure instanceof ApiError) {
    if (failure.code === 'version-conflict') return t(`admin.${screen}.error.version-conflict`);
    if (failure.code === 'validation-failed') return t(`admin.${screen}.error.validation`);
    if (failure.code === 'conflict' && screen === 'discount') {
      return t('admin.discount.error.conflict');
    }
  }
  return errorMessage(t, failure);
}
